import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-12934">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-12935">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-12936">
              <span className="text-xs font-bold text-indigo-800" data-id="element-12937">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-12938">
            <div className="text-center" data-id="element-12939">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-12940">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-12941">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-12942">→</div>
            <div className="text-center" data-id="element-12943">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-12944">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-12945">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-12946">
            <table className="w-full text-sm" data-id="element-12947">
              <thead data-id="element-12948">
                <tr className="bg-indigo-100/70" data-id="element-12949">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-12950">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-12951">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-12952">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-12953">
                      <td className="px-3 py-1.5" data-id="element-12954">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-12955">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-12956">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-12957">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-12958">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-12959">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-12960">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-12961">
            <span className="text-2xl" data-id="element-12962">📝</span>
            <div className="text-left" data-id="element-12963">
              <div className="text-sm font-bold text-indigo-900" data-id="element-12964">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-12965">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-12966">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-12967">
            <div className="text-center" data-id="element-12968">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-12969">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-12970">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-12971">→</div>
            <div className="text-center" data-id="element-12972">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-12973">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-12974">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-12975">
          <table className="w-full" data-id="element-12976">
            <thead data-id="element-12977">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-12978">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-12979">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-12980">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-12981">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-12982">
                    <td className="px-4 py-3" data-id="element-12983">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-12984">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-12985">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-12986">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-12987">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-12988">
            <div className="flex items-start gap-3" data-id="element-12989">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-12990">
                <span className="text-blue-600 text-lg" data-id="element-12991">🔍</span>
              </div>
              <div data-id="element-12992">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-12993">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12994">
                  De <strong data-id="element-12995">stam</strong> krijg je door <strong data-id="element-12996">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-12997">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-12998">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-12999">
              <div className="flex items-start gap-3" data-id="element-13000">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13001">
                  <span className="text-emerald-600 text-lg" data-id="element-13002">📋</span>
                </div>
                <div data-id="element-13003">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13004">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13005">
                    <p data-id="element-13006">
                      <strong data-id="element-13007">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13008">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-13009">
                      <strong data-id="element-13010">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13011">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13012">
                      <strong data-id="element-13013">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13014">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13015">
                      <strong data-id="element-13016">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13017">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13018">
              <div className="flex items-start gap-3" data-id="element-13019">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13020">
                  <span className="text-emerald-600 text-lg" data-id="element-13021">📋</span>
                </div>
                <div data-id="element-13022">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13023">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13024">
                    <p data-id="element-13025">
                      <strong data-id="element-13026">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-13027">
                      <strong data-id="element-13028">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13029">
                      <em data-id="element-13030">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13031">
              <div className="flex items-start gap-3" data-id="element-13032">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13033">
                  <span className="text-emerald-600 text-lg" data-id="element-13034">📋</span>
                </div>
                <div data-id="element-13035">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13036">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13037">
                    <p data-id="element-13038">
                      <strong data-id="element-13039">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-13040">
                      <strong data-id="element-13041">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13042">
                      <em data-id="element-13043">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13044">
            <div className="flex items-start gap-3" data-id="element-13045">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13046">
                <span className="text-amber-600 text-lg" data-id="element-13047">💡</span>
              </div>
              <div data-id="element-13048">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13049">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13050">
                  Bij <strong data-id="element-13051">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-13052">jij loopt</em> wordt{' '}
                  <em data-id="element-13053">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}